import React, {useContext, useEffect, useRef, useState} from 'react';
import {ScrollView, View} from 'react-native';
import {ActivityIndicator, Button, Dialog, Portal} from 'react-native-paper';
import MaterialCommunityIcons from 'react-native-vector-icons/MaterialCommunityIcons';
import PropTypes from 'prop-types';
import {Pressable, Text} from '~/components/controls';
import {service_request as serviceRequestApi} from '~/api/private';
import Grouping from '~/components/controls/3/Grouping';
import ServiceRequestContext from '../context';
import {useSelector} from '~/lib/hooks';

const money = (amount) =>
  typeof amount === 'number'
    ? new Intl.NumberFormat('nl-NL', {
        style: 'currency',
        currency: 'EUR',
      }).format(amount)
    : 'Prijs onbekend';

// Identical entries get occurrence keys; unrelated insertions keep existing keys.
export const keyProposalItems = (items, identify = (item) => item) => {
  const occurrences = new Map();
  return items.map((item) => {
    const identity = JSON.stringify(identify(item));
    const occurrence = occurrences.get(identity) ?? 0;
    occurrences.set(identity, occurrence + 1);
    return {item, key: `${identity}:${occurrence}`};
  });
};

const ProposalButton = ({request_id}) => {
  const {refresh} = useContext(ServiceRequestContext);
  const enabled = useSelector(
    (state) => state.user_settings?.service_request_proposal === true,
  );
  const canApply = useSelector(
    (state) => state.user_settings?.service_request_proposal_apply === true,
  );
  const [proposalId, setProposalId] = useState(null);
  const [snapshotId, setSnapshotId] = useState(null);
  const [snapshotExpired, setSnapshotExpired] = useState(false);
  const [applying, setApplying] = useState(false);
  const applyGuard = useRef(false);
  const [visible, setVisible] = useState(false);
  const [busy, setBusy] = useState(false);
  const [proposal, setProposal] = useState(null);
  const [error, setError] = useState(null);
  const run = useRef(0);
  const timer = useRef(null);

  useEffect(() => {
    setVisible(false);
    setBusy(false);
    setProposal(null);
    setProposalId(null);
    setSnapshotId(null);
    setSnapshotExpired(false);
    setError(null);
    return () => {
      run.current += 1;
      clearTimeout(timer.current);
    };
  }, [request_id]);

  const generate = async () => {
    if (!enabled || busy) return;
    const current = ++run.current;
    setVisible(true);
    setBusy(true);
    setError(null);
    setProposal(null);
    setProposalId(null);
    setSnapshotId(null);
    setSnapshotExpired(false);
    const started = Date.now();
    const fail = (message) => {
      if (current !== run.current) return;
      setBusy(false);
      setError(message);
    };
    try {
      const queued = await serviceRequestApi.proposal(request_id);
      if (current !== run.current) return;
      if (!queued?.success) throw new Error('enqueue');
      if (queued.result?.proposal) {
        setProposalId(queued.result.id);
        setSnapshotId(queued.result.article_snapshot?.id ?? null);
        setError(queued.result.article_resolution_error ?? null);
        setProposal(queued.result.proposal);
        setBusy(false);
        return;
      }
      const poll = async () => {
        try {
          const response = await serviceRequestApi.proposal(request_id, {
            poll: true,
          });
          if (current !== run.current) return;
          if (!response?.success) throw new Error('retrieve');
          if (response.result?.proposal) {
            setProposalId(response.result.id);
            setSnapshotId(response.result.article_snapshot?.id ?? null);
            setError(response.result.article_resolution_error ?? null);
            setProposal(response.result.proposal);
            setBusy(false);
          } else if (Date.now() - started >= 5 * 60 * 1000) {
            fail('Het voorstel is nog niet klaar. Probeer het later opnieuw.');
          } else {
            timer.current = setTimeout(poll, 3000);
          }
        } catch {
          fail('Het voorstel kon niet worden opgehaald. Probeer het opnieuw.');
        }
      };
      await poll();
    } catch {
      fail('Het voorstel kon niet worden aangevraagd. Probeer het opnieuw.');
    }
  };

  const dismiss = () => {
    if (applyGuard.current) return;
    run.current += 1;
    clearTimeout(timer.current);
    setBusy(false);
    setVisible(false);
  };

  const apply = async () => {
    if (
      !enabled ||
      !canApply ||
      applyGuard.current ||
      !proposalId ||
      !snapshotId ||
      snapshotExpired ||
      !proposal?.lines?.length
    )
      return;
    applyGuard.current = true;
    setApplying(true);
    setError(null);
    try {
      const response = await serviceRequestApi.apply_proposal(
        request_id,
        proposalId,
        snapshotId,
      );
      if (
        [
          'ARTICLE_SNAPSHOT_EXPIRED',
          'ARTICLE_SNAPSHOT_REQUIRED',
          'ARTICLE_SNAPSHOT_INVALID',
        ].includes(response?.code)
      ) {
        setSnapshotExpired(true);
        setError(
          response.code === 'ARTICLE_SNAPSHOT_EXPIRED'
            ? 'De artikelprijzen zijn verlopen. Vernieuw de artikelen en controleer het voorstel opnieuw.'
            : 'De artikelselectie ontbreekt of is ongeldig. Vernieuw de artikelen en controleer het voorstel opnieuw.',
        );
        return;
      }
      if (!response?.success) throw new Error('apply');
      setVisible(false);
      await refresh?.();
    } catch {
      setError(
        'Het voorstel kon niet worden overgenomen. Probeer het opnieuw.',
      );
    } finally {
      applyGuard.current = false;
      setApplying(false);
    }
  };

  const refreshArticles = async () => {
    if (busy || applying) return;
    const current = ++run.current;
    setBusy(true);
    setError(null);
    try {
      const response = await serviceRequestApi.proposal(request_id, {
        poll: true,
        refresh_articles: true,
      });
      if (current !== run.current) return;
      if (!response?.success || !response.result?.article_snapshot?.id)
        throw new Error('articles');
      setProposalId(response.result.id);
      setSnapshotId(response.result.article_snapshot.id);
      setProposal(response.result.proposal);
      setSnapshotExpired(false);
    } catch {
      if (current === run.current)
        setError(
          'De artikelen konden niet worden vernieuwd. Probeer het opnieuw.',
        );
    } finally {
      if (current === run.current) setBusy(false);
    }
  };

  const lines = [...(proposal?.lines ?? [])].sort((a, b) =>
    String(a.rob_code ?? '').localeCompare(String(b.rob_code ?? ''), 'nl', {
      numeric: true,
    }),
  );

  if (!enabled) return null;

  return (
    <>
      <Pressable
        accessibilityRole="button"
        accessibilityLabel="AI-voorstel maken"
        disabled={busy || applying}
        onPress={generate}
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          gap: 8,
          padding: 8,
        }}>
        <MaterialCommunityIcons name="auto-fix" size={24} color="#231fda" />
      </Pressable>
      <Portal>
        <Dialog
          visible={visible}
          onDismiss={dismiss}
          dismissable={!applying}
          style={{
            width: '90%',
            maxWidth: 800,
            maxHeight: '90%',
            alignSelf: 'center',
          }}>
          <Dialog.Title>AI-voorstel</Dialog.Title>
          <Dialog.ScrollArea style={{flexShrink: 1}}>
            <ScrollView contentContainerStyle={{paddingVertical: 16, gap: 16}}>
              {busy && (
                <>
                  <ActivityIndicator />
                  <Text>Het voorstel wordt geladen…</Text>
                </>
              )}
              {error && <Text accessibilityRole="alert">{error}</Text>}
              {proposal && (
                <>
                  <Text>
                    {proposal.license} · {proposal.mileage} km
                  </Text>
                  {keyProposalItems(proposal.warnings ?? []).map(
                    ({item: warning, key}) => (
                      <Text key={key}>{warning}</Text>
                    ),
                  )}
                  {proposal.lines?.length === 0 && (
                    <Text>Geen werkzaamheden voorgesteld.</Text>
                  )}
                  {keyProposalItems(
                    lines,
                    (line) =>
                      line.id ?? [
                        line.rob_code,
                        line.operation,
                        line.location,
                        line.description,
                      ],
                  ).map(({item: line, key}) => (
                    <Grouping
                      key={key}
                      header={`${line.rob_code} · ${line.description}`}
                      headerRight={
                        <Text style={{fontWeight: 'bold', textAlign: 'right'}}>
                          {money(line.price)}
                        </Text>
                      }
                      pressableStyle={{padding: 0}}>
                      <View style={{gap: 6}}>
                        <Text>
                          {[line.operation_description, line.reason_description]
                            .filter(Boolean)
                            .join(' · ')}
                        </Text>
                        {line.task && (
                          <Text>
                            Arbeid:{' '}
                            {line.task.duration != null
                              ? `${line.task.duration} uur · `
                              : ''}
                            {money(line.task.amount)}
                          </Text>
                        )}
                        {keyProposalItems(
                          line.articles ?? [],
                          (article) =>
                            article.id ??
                            article.itemMpId ??
                            article.reference ??
                            article.number ??
                            article.description,
                        ).map(({item: article, key}) => (
                          <Text key={key}>
                            {article.quantity} × {article.description} ·{' '}
                            {money(article.amount)}
                          </Text>
                        ))}
                        {line.approval != null && (
                          <Text>
                            Kans op goedkeuring:{' '}
                            {Math.round(line.approval * 100)}%
                          </Text>
                        )}
                        {line.rationale && <Text>{line.rationale}</Text>}
                        {keyProposalItems(line.warnings ?? []).map(
                          ({item: warning, key}) => (
                            <Text key={key}>{warning}</Text>
                          ),
                        )}
                      </View>
                    </Grouping>
                  ))}
                  <Text style={{fontWeight: 'bold'}}>
                    Totaal:{' '}
                    {money(
                      (proposal.lines ?? []).reduce(
                        (total, line) => total + (line.price ?? 0),
                        0,
                      ),
                    )}
                  </Text>
                </>
              )}
            </ScrollView>
          </Dialog.ScrollArea>
          <Dialog.Actions>
            {error && !proposal && (
              <Button onPress={generate}>Opnieuw proberen</Button>
            )}
            {proposal && (!snapshotId || snapshotExpired) && (
              <Button onPress={refreshArticles} disabled={busy || applying}>
                Artikelen vernieuwen
              </Button>
            )}
            <Button
              onPress={canApply ? apply : dismiss}
              loading={applying}
              disabled={
                applying ||
                (canApply &&
                  (busy ||
                    !proposalId ||
                    !snapshotId ||
                    snapshotExpired ||
                    !lines.length))
              }>
              {canApply ? 'Overnemen' : 'Sluiten'}
            </Button>
          </Dialog.Actions>
        </Dialog>
      </Portal>
    </>
  );
};
ProposalButton.propTypes = {request_id: PropTypes.string.isRequired};
export default ProposalButton;
